import { type FC, useCallback, useMemo, type MouseEvent } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import type { ButtonProps } from '@vakantiesnl/components/src/atoms/Button/Button.types';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AdlinkCell } from '@vakantiesnl/components/src/molecules/AdlinkCell';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import type { AdlinkValues } from '@vakantiesnl/services/src/gtm/adlinks';
import { adlinksFormatter } from '@vakantiesnl/services/src/util/adlinksFormatter';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type Adlinks as AdlinkTypes } from '@vakantiesnl/types';
import { useRouter } from 'next/router';

import { useCustomStyles } from './AdlinksContainer.style';

export interface AdlinksContainerProps {
	/** Array of the adlinks to show */
	adlinks: AdlinkTypes.Adlink[];
	/** Name of the acco */
	accommodationName?: string;
	/** The variant of the toggle button if displayed */
	toggleVariant?: ButtonProps['variant'];
	/** On click function for toggle button if displayed */
	onToggleClick?: (clickEvent: MouseEvent<HTMLButtonElement>) => void;
	/** Title component to display */
	titleToShow?: JSX.Element;
	/** Alternative description component to display */
	descriptionToShow?: JSX.Element;
	/** Whether to show the default description */
	showDefaultDescription?: boolean;
	/** Whether to show the adlinks list without limit */
	showExpanded?: boolean;
	/** On click function for individual adlinks */
	onAdlinkClick?: (adlinkValues: AdlinkValues) => void;
	/** Classname to add to the component */
	className?: string;
	/** Content of the Google Analytics cookie */
	googleAnalyticsCookie?: string;
	/** Toggle for showing/hiding a list of adlinks */
	showAdlinksContent?: boolean;
	/** Wether the acco is saved in the favorites (probably only needed for the NonBookableView component */
	isFavorite?: boolean;
	onFavoriteClick?: () => void;
	/** Conditionally hide/show favorites. Used for Bookable/non bookable hybrid pages */
	showFavorite?: boolean;
}

const COLLAPSED_LIMIT = 4;

export const AdlinksContainer: FC<AdlinksContainerProps> = ({
	adlinks,
	accommodationName,
	toggleVariant = 'primary',
	onToggleClick,
	titleToShow,
	showExpanded = false,
	descriptionToShow,
	showDefaultDescription = true,
	onAdlinkClick,
	className,
	googleAnalyticsCookie,
	showAdlinksContent = true,
	isFavorite,
	onFavoriteClick,
	showFavorite,
}) => {
	const { isTabletOrIsDesktop } = useBreakpoints();

	const { classes, cx } = useCustomStyles({ toggleVariant });
	const { asPath } = useRouter();
	const microCopies = useMicroCopyContext();

	const formattedAdlinks = useMemo(
		(): AdlinkTypes.FormattedAdlink[] =>
			adlinksFormatter(adlinks, asPath.split('#')[0], microCopies, onAdlinkClick, googleAnalyticsCookie),
		[adlinks, asPath, googleAnalyticsCookie, microCopies, onAdlinkClick],
	);

	const displayLimit = useMemo(
		() => (showExpanded ? formattedAdlinks.length : COLLAPSED_LIMIT),
		[showExpanded, formattedAdlinks.length],
	);

	const renderAdlinks = useCallback(
		(formattedAdlinks: AdlinkTypes.FormattedAdlink[], limit: number): JSX.Element[] =>
			formattedAdlinks.slice(0, limit).map((adlink, index) => <AdlinkCell key={index} adlink={adlink} />),
		[],
	);

	if (!formattedAdlinks.length) return null;

	return (
		<div className={cx(classes.adLinksContainer, className)}>
			{!!titleToShow && titleToShow}
			{showDefaultDescription && !!accommodationName && (
				<Typography variant="bodySm" as="p" data-cy="adlinks-subheading">
					{inject(microCopies['adlinks.subHeading'], {
						name: accommodationName,
					})}
				</Typography>
			)}
			{!!descriptionToShow && descriptionToShow}
			{showAdlinksContent && (
				<div data-cy="adlinks">
					<ul className={classes.adlinksList} data-cy="adlinks-list">
						{renderAdlinks(formattedAdlinks, displayLimit)}
					</ul>
					{!showExpanded && formattedAdlinks.length > displayLimit && (
						<div className={classes.buttonContainer}>
							<Button
								onClick={onToggleClick}
								className={classes.showMoreButton}
								aria-label={microCopies['adlinks.showAll']}
								data-qa="button-show-more"
								variant={toggleVariant}
							>
								{toggleVariant === 'primary'
									? microCopies['adlinks.showAll']
									: microCopies['common.toAllAdlinks']}
							</Button>
							{showFavorite && (
								<Button
									leadingIcon={isFavorite ? 'favoriteFilled' : 'favorite'}
									className={cx(classes.favoriteButton, {
										[classes.favoriteButtonActive]: isFavorite,
									})}
									variant="secondary"
									size={isTabletOrIsDesktop ? 'medium' : 'small'}
									onClick={onFavoriteClick}
								/>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};
